import { Fragment, useRef, useState, useEffect } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { createPortal } from 'react-dom';

interface IconOption {
  name: string;
  icon: string;
}

interface IconSelectProps {
  value: string;
  options: IconOption[];
  onChange: (value: string) => void;
  label?: string;
}

export default function IconSelect({ value, options, onChange, label }: IconSelectProps) {
  const selectedOption = options.find(option => option.icon === value) || options[0];
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownStyles, setDropdownStyles] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownStyles({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, []);

  return (
    <Listbox value={value} onChange={onChange}>
      {({ open }) => (
        <div className="relative">
          {label && (
            <Listbox.Label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              {label}
            </Listbox.Label>
          )}
          <Listbox.Button
            ref={buttonRef}
            className="relative w-full rounded-lg border border-gray-300 dark:border-dark-border 
              bg-white dark:bg-dark-bg-light py-2.5 pl-3 pr-10 text-left shadow-sm 
              focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 
              dark:text-dark-text-primary text-sm transition-colors duration-200"
          >
            <div className="flex items-center">
              <i className={`fas ${selectedOption.icon} text-gray-400 dark:text-dark-text-secondary mr-2`} />
              <span className="block truncate">{selectedOption.name}</span>
            </div>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <i className="fas fa-chevron-down text-gray-400 dark:text-dark-text-secondary text-xs" />
            </span>
          </Listbox.Button>

          {open && createPortal(
            <Transition
              show={true}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                style={{
                  position: 'absolute',
                  top: dropdownStyles.top,
                  left: dropdownStyles.left,
                  width: dropdownStyles.width,
                }}
                className="z-[9999] max-h-[400px] overflow-auto rounded-lg 
                  bg-white dark:bg-dark-bg-light py-1 text-sm shadow-xl ring-1 ring-black ring-opacity-5 
                  focus:outline-none scrollbar-thin scrollbar-track-transparent
                  scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400
                  dark:scrollbar-thumb-gray-600 dark:hover:scrollbar-thumb-gray-500"
              >
                {options.map((option) => (
                  <Listbox.Option
                    key={option.icon}
                    value={option.icon}
                    className={({ active }) => `
                      relative cursor-pointer select-none py-2.5 pl-3 pr-9
                      ${active ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' : 
                        'text-gray-900 dark:text-dark-text-primary'}
                    `}
                  >
                    {({ selected, active }) => (
                      <>
                        <div className="flex items-center">
                          <i className={`fas ${option.icon} ${
                            active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-dark-text-secondary'
                          } mr-2`} />
                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                            {option.name}
                          </span>
                        </div>

                        {selected && (
                          <span className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                            active ? 'text-primary-600 dark:text-primary-400' : 'text-primary-600 dark:text-primary-400'
                          }`}>
                            <i className="fas fa-check text-sm" />
                          </span>
                        )}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>,
            document.body
          )}
        </div>
      )}
    </Listbox>
  );
} 